/**
 * View access — single source of truth for "is this view_code allowed for the
 * current profile", used everywhere a view can be reached OUTSIDE the sidebar
 * (App.jsx render guard, Ctrl+K search, widget navigation).
 *
 * The sidebar already filters by `profile.modules` server-side
 * (backend/services/sidebar_resolver_service.mjs: `allowedModules.includes(v.view_code)`).
 * This file mirrors that same rule on the client so every OTHER navigation path
 * (a widget's onNavigate, Ctrl+K, a deep-link, a stale `localStorage` view) is held
 * to the same rule — a hidden menu entry is not a permission (see project memory
 * bug_driver_no_role_guard_p0.md).
 */

// Views always reachable regardless of profile.modules — pure UI chrome, no data exposure.
// `dashboard` n'y est PLUS (2026-09-14) : un profil qui ne le liste pas (le conducteur)
// n'atterrit pas dessus et ne peut pas y revenir par Ctrl+H ou un deep-link — il suit le
// menu, comme tout le reste. Un profil sans liste de modules le garde (compatibilité).
export const ALWAYS_ALLOWED_VIEWS = [];

// A handful of view_codes rendered by App.jsx diverge from the module code that
// gates them (widgets/search navigate to the view_code, profiles declare the module).
const VIEW_TO_MODULE_ALIAS = {
  calendar: 'events',
  'insurance-config': 'insurance',
};

// Views not part of any `ui_profiles.modules` list — reached only through an
// isAdmin/isSuperuser check (mirrors the adminOnly/superuserOnly flags Sidebar.jsx
// reads from app_views). Listed here so a deep-link or stale view can't bypass that.
// `data_export` n'y figure PAS : la vue vit dans le groupe « Administratif » (non réservé)
// et se gouverne par `profile.modules`, comme dans le menu. L'y laisser montrait l'entrée
// au responsable d'U.T. puis lui refusait l'écran (mesuré 2026-09-13). Le périmètre des
// lignes exportées est tenu côté serveur (test_data_export.mjs).
const ADMIN_OR_SUPERUSER_VIEWS = [
  'settings', 'superadmin', 'billing', 'feature_monitor',
  'sidebar-config', 'profile_admin', 'missions', 'driver-home',
];

/**
 * @param {string} viewCode
 * @param {object} profile - current UI profile (from useProfile())
 * @param {object} [options]
 * @param {boolean} [options.isAdmin]
 * @param {boolean} [options.isSuperuser]
 * @param {(name: string) => boolean} [options.hasFeature] - tenant feature flags (ADR-011)
 */
export function isViewAllowed(viewCode, profile, options = {}) {
  if (!viewCode) return false;
  const { isAdmin = false, isSuperuser = false, hasFeature } = options;

  // Superusers bypass everywhere else in the app (see CLAUDE.md, isolation matrix).
  if (isSuperuser) return true;

  if (ALWAYS_ALLOWED_VIEWS.includes(viewCode)) return true;

  if (viewCode === 'dashboard') {
    const modules = profile?.modules;
    return !Array.isArray(modules) || modules.length === 0 || modules.includes('dashboard');
  }

  if (viewCode === 'e_invoices') {
    return typeof hasFeature === 'function' && hasFeature('e_invoices');
  }

  if (ADMIN_OR_SUPERUSER_VIEWS.includes(viewCode)) {
    return isAdmin;
  }

  const moduleCode = VIEW_TO_MODULE_ALIAS[viewCode] || viewCode;
  return Boolean(profile?.modules?.includes(moduleCode));
}

/**
 * La vue d'ACCUEIL d'un profil — celle qu'on ouvre après le login, au Ctrl+H, et
 * quand une vue refusée est renvoyée. Règle : le pipeline s'il est activé et permis,
 * sinon le tableau de bord s'il est permis, sinon le PREMIER module du profil.
 * C'est ce qui fait atterrir le conducteur sur « Mon véhicule » sans une ligne de
 * code propre à lui : son profil ne liste pas `dashboard`, donc il n'y va pas.
 *
 * @param {object} profile - current UI profile (from useProfile())
 * @param {object} [options]
 * @param {boolean} [options.pipelineEnabled]
 * @param {boolean} [options.isAdmin]
 * @param {boolean} [options.isSuperuser]
 * @param {(name: string) => boolean} [options.hasFeature]
 * @returns {string} view_code
 */
export function getHomeView(profile, options = {}) {
  const { pipelineEnabled = false, ...access } = options;
  const allowed = (v) => isViewAllowed(v, profile, access);
  if (pipelineEnabled && allowed('pipeline')) return 'pipeline';
  if (allowed('dashboard')) return 'dashboard';
  const first = (profile?.modules || []).find((m) => allowed(m));
  return first || 'dashboard';
}
